import { Radio, RadioGroup, RadioProvider } from '@ariakit/react'
import { FC, useId, useMemo } from 'react'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryFont } from '../../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'
import { toEmbroideryFontFamily } from '../../../../../../lib/embroideryFonts'
import { Label, SelectedOptionText } from '../../shared-form-styles'
import { ValidationMessage } from '../../ValidationMessage/ValidationMessage'

import { ButtonsWrapper, FontSelectionButton } from './styles'

type FontKey = EmbroideryFont['key']

export const FontSelector: FC<{
  disabled?: boolean
  errorMessage?: string
  labelId: string
  onSelectFont(font: EmbroideryFont): void
  selectedFontKey: FontKey | undefined
  fonts: EmbroideryFont[]
  /** Styles the selected button with an outline instead of a fill. Only the new Embroidery Modal wants this. */
  outlineWhenSelected?: boolean
}> = ({
  errorMessage,
  labelId,
  onSelectFont,
  selectedFontKey,
  fonts,
  disabled,
  outlineWhenSelected,
}) => {
  const { embroidery: fixtures } = useTranslation()
  const errorId = useId()
  const selectedFont = useMemo(
    () => fonts.find(f => f.key === selectedFontKey),
    [fonts, selectedFontKey]
  )

  return (
    <div>
      <Label id={labelId} disabled={disabled}>
        {fixtures.modal.forms.fontSelector.label}{' '}
        <SelectedOptionText disabled={disabled}>{selectedFont?.displayName}</SelectedOptionText>
      </Label>

      <RadioProvider
        value={selectedFont?.key ?? null}
        setValue={key => {
          const font = fonts.find(f => f.key === key)
          if (font) onSelectFont(font)
        }}
      >
        <RadioGroup
          render={
            <ButtonsWrapper
              $disabled={disabled ?? false}
              aria-labelledby={labelId}
              aria-describedby={errorMessage ? errorId : undefined}
            />
          }
        >
          {fonts.map(font => (
            <Radio
              accessibleWhenDisabled
              disabled={disabled}
              key={font.key}
              value={font.key}
              render={
                <FontSelectionButton
                  fontFamily={toEmbroideryFontFamily(font.designFile)}
                  isSelected={font.key === selectedFont?.key}
                  outlineWhenSelected={outlineWhenSelected}
                  type='button'
                >
                  {font.displayName}
                </FontSelectionButton>
              }
            />
          ))}
        </RadioGroup>
      </RadioProvider>

      <ValidationMessage id={errorId} message={errorMessage} />
    </div>
  )
}
